import * as React from 'react';
import Alert from '@material-ui/lab/Alert';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import type { AxiosError } from 'axios';
import axios from 'axios';

import CompanyInformation from './CompanyInformation';
import JobGrouping from './UpsertJobGrouping';
import type {
  IRepair, IRepairGrouping, ICompany, IStore,
} from './MassReceiveInterfaces';
import AllGroupings from './AllGroupings';
import RepairsScanned from './RepairsScanned';
import RestartScanDialog from './RestartScanDialog';
import useEventListener from '../../lib/useEventListener';

const useStyles = makeStyles((theme) => createStyles({
  root: {
    flexGrow: 1,
  },
  MassReceiveCard: {
    height: 180,
  },
  finishGroupingButton: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
}));

const MassReceive: React.FC = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [repairGroupings, setRepairGroupings] = React.useState<IRepairGrouping[]>([]);
  const [unscannedRepairs, setUnscannedRepairs] = React.useState<IRepair[]>([]);
  const [scannedRepairs, setScannedRepairs] = React.useState<IRepair[]>([]);
  const [nextGroupNumber, setNextGroupNumber] = React.useState<number>(1);
  const [stores, setStores] = React.useState<IStore[]>([]);
  const [company, setCompany] = React.useState<ICompany | null>(null);
  const [store, setStore] = React.useState<IStore | null>(null);
  const [trackingNumber, setTrackingNumber] = React.useState<string>();
  const [storeRepairNumber, setStoreRepairNumber] = React.useState<string>();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [showRestartScanDialog, setShowRestartScanDialog] = React.useState<boolean>(false);

  const handleRestartScanClick = () => {
    setShowRestartScanDialog(true);
  };

  const addNewRepairGrouping = (repairGrouping: IRepairGrouping) => {
    setRepairGroupings(
      [repairGrouping, ...repairGroupings],
    );

    setNextGroupNumber(repairGrouping.groupNumber + 1);
  };

  function getRange(start: number, stop: number, step = 1) {
    return Array.from(
      { length: (stop - start) / step + 1 },
      (_, i) => start + (i * step),
    );
  }

  const handleFinishGroupingClick = () => {
    let repairArray = [...unscannedRepairs];
    repairGroupings.forEach((rg) => {
      const barcodesInGroup = getRange(rg.startingBarcode, rg.endingBarcode);
      const repairsFromBarcodes: IRepair[] = barcodesInGroup.map(
        (bc) => ({ id: '', barcode: String(bc), groupNumber: rg.groupNumber }),
      );
      repairArray = [...repairArray, ...repairsFromBarcodes];
    });
    setUnscannedRepairs(
      [...repairArray],
    );
  };

  const scanRepair = (inputBarcode: string) => {
    const unscannedRepairIndex = unscannedRepairs.findIndex(
      (repair) => repair?.barcode === inputBarcode,
    );
    const scannedRepairIndex = scannedRepairs.findIndex(
      (repair) => repair?.barcode === inputBarcode,
    );
    if (unscannedRepairIndex === -1) {
      if (scannedRepairIndex === -1) {
        enqueueSnackbar('Scanned barcode does not exist in repair groups.', { persist: false, variant: 'error' });
      }
    } else {
      const scannedRepair = unscannedRepairs[unscannedRepairIndex];
      const tempStateUnscanned: IRepair[] = unscannedRepairs;
      tempStateUnscanned.splice(unscannedRepairIndex, 1);
      setUnscannedRepairs([...tempStateUnscanned]);
      const tempStateScanned: IRepair[] = scannedRepairs;
      tempStateScanned.push(scannedRepair);
      setScannedRepairs([...tempStateScanned]);
    }
  };

  const restartScan = (scannedRepairsInput: IRepair[]) => {
    setUnscannedRepairs([...unscannedRepairs, ...scannedRepairsInput]);
    setScannedRepairs([]);
  };

  const handleRestartScanDialogClose = (restartScanParam?: boolean) => {
    if (restartScanParam) {
      restartScan(scannedRepairs);
    }
    // close dialog
    setShowRestartScanDialog(false);
  };

  const clearAllState = () => {
    setUnscannedRepairs([]);
    setScannedRepairs([]);
    setRepairGroupings([]);
    setTrackingNumber('');
    setStoreRepairNumber('');
    setStore(null);
    setCompany(null);
    setNextGroupNumber(1);
  };

  let barcodeBuffer = '';

  const handleBarcodeInput = (e: KeyboardEvent) => {
    if (e.key !== 'Shift') {
      barcodeBuffer = `${barcodeBuffer}${e.key}`;
      if (barcodeBuffer.toLowerCase().includes('in:')) {
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('{*|}')) {
        scanRepair(barcodeBuffer.replace('{*|}', ''));
        barcodeBuffer = '';
      }
    }
  };

  const handleCreateRepairs = () => {
    const repairsToSend = scannedRepairs.map(
      (repair) => {
        const repairMap = repair;
        repairMap.jobs = repairGroupings.find(
          (value) => value.groupNumber === repair.groupNumber,
        )?.jobsAssigned.map(
          (value) => value,
        );
        return repairMap;
      },
    );
    setLoading(true);
    axios.post('/massReceive/saveGroupedRepairs', {
      storeId: store?.storeId,
      repairs: repairsToSend,
      barcodes: scannedRepairs.map((repair) => repair.barcode),
      trackingNumber,
      storeRepairNumber,
    })
      .then(() => {
        setLoading(false);
        enqueueSnackbar('Repairs created successfully', { variant: 'success' });
        clearAllState();
      })
      .catch((error: AxiosError) => {
        setLoading(false);
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });
  };

  const handleSelectedCompanyChange = (selectedCompany: ICompany | null) => {
    // clear current values
    setStore(null);
    setStores([]);
    axios.get<IStore[]>('/opener/getStoresForCompany', {
      params: {
        companyId: selectedCompany?.companyId,
      },
    })
      .then((response: { data: IStore[]; }) => {
        // set new values
        setCompany(selectedCompany);
        setStores(response.data);
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });
  };

  useEventListener('keydown', handleBarcodeInput);

  return (
    <Box className={classes.root}>
      <Grid container spacing={0}>
        <Grid item xs={1} />
        <Grid item xs={8} container spacing={4}>
          <Grid item xs={12}>
            <Alert severity="info">{'Fill out information about the company and assign job codes to groupings.'}</Alert>
          </Grid>
          <Grid item xs={12}>
            <CompanyInformation
              store={store}
              stores={stores}
              company={company}
              setStore={setStore}
              handleSelectedCompanyChange={handleSelectedCompanyChange}
              trackingNumber={trackingNumber}
              setTrackingNumber={setTrackingNumber}
              storeRepairNumber={storeRepairNumber}
              setStoreRepairNumber={setStoreRepairNumber}
              elemDisabled={scannedRepairs.length > 0}
            />
          </Grid>
          <Grid item xs={12}>
            <JobGrouping
              addNewRepairGrouping={addNewRepairGrouping}
              groupNumber={nextGroupNumber}
              elemDisabled={scannedRepairs.length > 0}
            />
          </Grid>
          <Grid item xs={12}>
            <AllGroupings
              repairGroupings={repairGroupings}
              setRepairGroupings={setRepairGroupings}
            />
          </Grid>
          <Grid item container direction="row-reverse">
            <Grid item>
              <Button
                disabled={repairGroupings.length === 0
                  || unscannedRepairs.length > 0
                  || scannedRepairs.length > 0}
                fullWidth
                id="finish-grouping-repair-button"
                variant="contained"
                color="secondary"
                className={classes.finishGroupingButton}
                onClick={() => handleFinishGroupingClick()}
              >
                {'FINISH GROUPING REPAIRS'}
              </Button>
            </Grid>
          </Grid>

          {(unscannedRepairs.length > 0 || scannedRepairs.length > 0) && (
            <>
              <Grid item xs={12}>
                <Alert severity="info">{'Apply barcode to each repair, and scan in. If an issue with a barcode arises, start scanning over.'}</Alert>
              </Grid>
              <RepairsScanned
                unscannedRepairs={unscannedRepairs}
                scannedRepairs={scannedRepairs}
                scanRepair={scanRepair}
                loading={loading}
              />
              <Grid container direction="row" justifyContent="flex-end" item spacing={1}>
                <Box pr={2}>
                  <Button
                    autoFocus
                    variant="text"
                    id="restart-scanning-button"
                    color="secondary"
                    onClick={handleRestartScanClick}
                  >
                    {'START SCANNING OVER'}
                  </Button>
                </Box>
                <Button
                  id="create-repairs-button"
                  variant="contained"
                  color="primary"
                  onClick={handleCreateRepairs}
                  disabled={unscannedRepairs.length > 0 || loading}
                  style={{ minWidth: '8.75rem' }}
                >
                  { loading ? (
                    <CircularProgress size={25} />
                  ) : 'CREATE REPAIRS'}
                </Button>
              </Grid>
            </>
          )}
          <RestartScanDialog
            showModal={showRestartScanDialog}
            onClose={handleRestartScanDialogClose}
          />
        </Grid>
        <Grid item xs={3} />
      </Grid>
    </Box>
  );
};
export default MassReceive;
